import { RIYAL } from "@/lib/currency";
import { useMemo } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatNumber } from "@/lib/formatters";
import { format } from "date-fns";
import {
  generateAutoSplitSchedule,
  deriveCanonicalTotal,
  isRentVatApplicable,
  paymentsPerYearForFrequency,
} from "@/lib/contractHelpers";
import { useProperties } from "@/hooks/useProperties";
import { useUnits } from "@/hooks/useUnits";
import { FlexiblePaymentEditor } from "@/components/contracts/FlexiblePaymentEditor";
import {
  ContractWizardFormData,
  PAYMENT_COUNTS,
  PAYMENT_METHODS,
  PaymentMethod,
  PaymentScheduleItem,
  RentBasis,
} from "./types";

interface PaymentInfoStepProps {
  formData: ContractWizardFormData;
  updateFormData: (updates: Partial<ContractWizardFormData> | ((prev: ContractWizardFormData) => ContractWizardFormData)) => void;
  errors: Record<string, string>;
  setErrors: (errors: Record<string, string>) => void;
}

const PAYMENT_FREQUENCY_OPTIONS = [
  { value: "monthly",       label: "شهري",         labelEn: "Monthly" },
  { value: "quarterly",     label: "ربع سنوي",     labelEn: "Quarterly" },
  { value: "semi-annually", label: "نصف سنوي",     labelEn: "Semi-Annually" },
  { value: "annually",      label: "سنوي",          labelEn: "Annually" },
  { value: "custom",        label: "مرن / مخصص",   labelEn: "Flexible / Custom" },
] as const;

export function PaymentInfoStep({
  formData,
  updateFormData,
  errors,
  setErrors,
}: PaymentInfoStepProps) {
  const { t, language } = useLanguage();

  const clearError = (field: string) => {
    if (errors[field]) {
      const newErrors = { ...errors };
      delete newErrors[field];
      setErrors(newErrors);
    }
  };

  // Derive the lease purpose the SAME way the Review step and the backend do:
  // classify by the selected unit's purpose, falling back to the property's
  // purpose. The rent VAT rate is 0 for residential leases, 15% otherwise.
  const { data: properties = [] } = useProperties();
  const { data: units = [] } = useUnits(formData.propertyId || undefined);
  const primaryUnitId =
    formData.units?.find((r) => r.unitId)?.unitId || formData.unitId;
  const selectedUnit = units.find((u) => u.id === primaryUnitId);
  const selectedProperty = properties.find((p) => p.id === formData.propertyId);
  const purpose = selectedUnit?.purpose ?? selectedProperty?.purpose;
  const rentVatRate = isRentVatApplicable(purpose) ? 15 : 0;

  const isCustom = formData.paymentFrequency === "custom";
  // `totalAmount` is what the user entered: either the full-term total
  // (rentBasis='term_total') or the annual rate (rentBasis='annual').
  const enteredAmount = Number(formData.totalAmount) || 0;
  // `paymentsCount` is payments-PER-YEAR and is the SINGLE source of truth for
  // the schedule (it is what the create-wizard submit and generateAutoSplit
  // read, and it supports the 3/6 options the named frequencies don't). The
  // Payment Frequency dropdown writes INTO paymentsCount on change, so changing
  // frequency now recomputes this preview — fixing the "frequency change
  // doesn't update the preview" bug — without a second, divergent driver.
  const paymentsPerYear = Number(formData.paymentsCount) || 0;

  // Contract duration in months — single source of truth (was duplicated in
  // the schedule memo and the flexible-target IIFE, which risked divergence).
  const contractMonths = useMemo(() => {
    const durationValue = Number(formData.durationValue) || 12;
    switch (formData.durationUnit) {
      case "day":
        return Math.max(1, Math.floor(durationValue / 30));
      case "year":
        return durationValue * 12;
      case "month":
      default:
        return durationValue;
    }
  }, [formData.durationValue, formData.durationUnit]);

  /**
   * ONE canonical full-term total drives all three consumers:
   *   1. The reconciliation target in FlexiblePaymentEditor.
   *   2. The auto-split schedule (so they can never disagree).
   *   3. The summary card header.
   *
   * The wizard's amount field is the ANNUAL rent, so rentBasis defaults to
   * 'annual' and deriveCanonicalTotal() multiplies the full years and prorates
   * the final partial period on REGA's 364-day year. (A future 'term_total'
   * toggle lets advanced users enter a lump-sum full-term total instead.)
   */
  const canonicalTotal = useMemo(
    () =>
      deriveCanonicalTotal(enteredAmount, formData.rentBasis, {
        startDate: formData.startDate,
        endDate: formData.endDate,
        contractMonths,
      }),
    [formData.rentBasis, enteredAmount, formData.startDate, formData.endDate, contractMonths],
  );

  // Generate equal-split payment schedule preview (non-custom modes).
  // Uses canonicalTotal so the preview table always sums to the target.
  const paymentSchedule = useMemo((): PaymentScheduleItem[] => {
    if (isCustom) return [];
    const firstDate = formData.firstPaymentDate;
    return generateAutoSplitSchedule(canonicalTotal, paymentsPerYear, contractMonths, firstDate ?? "");
  }, [
    isCustom,
    canonicalTotal,
    paymentsPerYear,
    formData.firstPaymentDate,
    contractMonths,
  ]);

  // Each payment preview amount (non-custom, for the info hint only) — read
  // directly off the generated schedule's first row so the hint can NEVER
  // disagree with the table below it. The old formula (canonicalTotal /
  // paymentsPerYear) is only correct for a whole number of years; the actual
  // schedule divides by totalPayments = ceil(contractMonths/12 ×
  // paymentsPerYear), which differs for any partial-year term — e.g. a 34-
  // month annual-rent contract showed one preview number in the hint and a
  // different (correct) one in the table below it.
  const paymentAmountPreview = paymentSchedule[0]?.amount ?? 0;

  const formatDisplayDate = (dateStr: string) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return format(date, "d/M/yyyy");
  };

  // The rent-basis toggle stays hidden for now: the amount field is the ANNUAL
  // rent, so rentBasis is fixed to 'annual' (deriveCanonicalTotal multiplies the
  // full years and prorates the partial period). A later phase persists
  // contracts.rent_basis and surfaces this toggle so users can opt into entering
  // a lump-sum 'term_total' instead.
  const SHOW_RENT_BASIS_TOGGLE: boolean = false;

  return (
    <div className="space-y-6">
      {/* Rent Basis Toggle — hidden until PR2 (see SHOW_RENT_BASIS_TOGGLE above).
          When enabled it determines how the entered amount is interpreted. */}
      {SHOW_RENT_BASIS_TOGGLE && (
      <div className="space-y-2">
        <Label>{t("أساس الإيجار", "Rent Basis")} <span className="text-destructive">*</span></Label>
        <Select
          value={formData.rentBasis}
          onValueChange={(val) => updateFormData({ rentBasis: val as RentBasis })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="term_total">
              {t("إجمالي قيمة العقد (كما هو متفق)", "Total for the term (as agreed)")}
            </SelectItem>
            <SelectItem value="annual">
              {t("إيجار سنوي (احتساب تناسبي)", "Annual rate (prorate partial periods)")}
            </SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          {formData.rentBasis === "annual"
            ? t(
                "المبلغ المدخل هو الإيجار السنوي — يُحسب الإجمالي تناسبياً على أساس 364 يوماً (معيار ريجا)",
                "Entered amount is the annual rate — total is prorated using 364-day REGA year"
              )
            : t(
                "المبلغ المدخل هو الإجمالي الكلي للعقد — لا يطبق أي تناسب",
                "Entered amount is the full contract total — no proration applied"
              )}
        </p>
      </div>
      )}

      {/* Summary Card — canonical full-term ex-VAT total. */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="p-4 space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">
              {t("إجمالي قيمة العقد (قبل الضريبة)", "Total Contract Value (excl. VAT)")}
            </span>
            <span className="text-xl font-bold text-primary">
              {formatNumber(canonicalTotal)}&nbsp;{RIYAL}
            </span>
          </div>
          {/* When annual basis, also show the entered annual rate for clarity. */}
          {formData.rentBasis === "annual" && canonicalTotal !== enteredAmount && enteredAmount > 0 && (
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{t("الإيجار السنوي (قبل الضريبة)", "Annual Rent (excl. VAT)")}</span>
              <span>
                {formatNumber(enteredAmount)}&nbsp;{RIYAL} ×{" "}
                {/* Actual term in years (1 decimal for partial periods). */}
                {formatNumber(Math.round((enteredAmount > 0 ? canonicalTotal / enteredAmount : 0) * 10) / 10)}{" "}
                {t("سنة", "yr")}
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment Frequency */}
      <div className="space-y-2">
        <Label>{t("تكرار الدفع", "Payment Frequency")} <span className="text-destructive">*</span></Label>
        <Select
          value={formData.paymentFrequency}
          onValueChange={(val) => {
            const freq = val as ContractWizardFormData["paymentFrequency"];
            const perYear = paymentsPerYearForFrequency(freq);
            updateFormData({
              paymentFrequency: freq,
              // Keep paymentsCount (payments-PER-YEAR) in sync with the chosen
              // frequency. paymentsCount is what the create-wizard submit and
              // the schedule generator read, so syncing it here makes the
              // preview, the saved schedule, and this dropdown agree — fixing
              // the "frequency change doesn't recompute the preview" bug.
              ...(freq !== "custom" && perYear > 0 ? { paymentsCount: String(perYear) } : {}),
              // Reset custom schedule when switching away from custom
              ...(val !== "custom" ? { customPaymentSchedule: [] } : {}),
            });
          }}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAYMENT_FREQUENCY_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {t(opt.label, opt.labelEn)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isCustom ? (
        /* ── Custom mode: flexible payment editor ── */
        <div className="space-y-3">
          <Label className="text-base font-medium">
            {t("جدول الدفعات المخصص", "Custom Payment Schedule")}
          </Label>
          {errors.customPaymentSchedule && (
            <p className="text-sm text-destructive">{errors.customPaymentSchedule}</p>
          )}
          <FlexiblePaymentEditor
            payments={formData.customPaymentSchedule}
            onChange={(payments) => {
              updateFormData({ customPaymentSchedule: payments });
              clearError("customPaymentSchedule");
            }}
            targetTotal={canonicalTotal}
            startDate={formData.startDate}
            endDate={formData.endDate}
            rentVatRate={rentVatRate}
          />
        </div>
      ) : (
        /* ── Standard mode: equal-split fields ── */
        <>
          {/* Row 1: Number of Payments, First Payment Date */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("عدد الدفعات", "Number of Payments")} <span className="text-destructive">*</span></Label>
              <Select
                value={formData.paymentsCount}
                onValueChange={(val) => {
                  // Keep the Payment Frequency dropdown label in sync with the
                  // chosen per-year count (4→quarterly, 2→semi-annually, …).
                  // 3 and 6 have no named frequency, so leave frequency as-is
                  // for those; the schedule is driven by paymentsCount anyway.
                  const FREQ_BY_PER_YEAR: Record<string, ContractWizardFormData["paymentFrequency"]> = {
                    "12": "monthly",
                    "4": "quarterly",
                    "2": "semi-annually",
                    "1": "annually",
                  };
                  const matchedFreq = FREQ_BY_PER_YEAR[val];
                  updateFormData({
                    paymentsCount: val,
                    ...(matchedFreq ? { paymentFrequency: matchedFreq } : {}),
                  });
                  clearError("paymentsCount");
                }}
              >
                <SelectTrigger className={errors.paymentsCount ? "border-destructive" : ""}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_COUNTS.map((count) => (
                    <SelectItem key={count.value} value={count.value}>
                      {t(count.label, count.labelEn)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.paymentsCount && <p className="text-sm text-destructive">{errors.paymentsCount}</p>}

              {/* Payment Amount Preview — derived from the canonical total. */}
              {paymentsPerYear > 0 && paymentAmountPreview > 0 && (
                <p className="text-xs text-muted-foreground">
                  {t("قيمة كل دفعة:", "Each payment:")}{" "}
                  <span className="font-semibold text-primary">
                    {formatNumber(Math.round(paymentAmountPreview))}&nbsp;{RIYAL}
                  </span>
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>{t("تاريخ أول دفعة", "First Payment Date")} <span className="text-destructive">*</span></Label>
              <DatePicker
                value={formData.firstPaymentDate}
                onChange={(date) => {
                  updateFormData({ firstPaymentDate: date });
                  clearError("firstPaymentDate");
                }}
                className={errors.firstPaymentDate ? "border-destructive" : ""}
              />
              {errors.firstPaymentDate && <p className="text-sm text-destructive">{errors.firstPaymentDate}</p>}
            </div>
          </div>

          {/* Row 2: Payment Method */}
          <div className="space-y-2">
            <Label>{t("طريقة الدفع المفضلة", "Preferred Payment Method")}</Label>
            <Select
              value={formData.preferredPaymentMethod}
              onValueChange={(val) => updateFormData({ preferredPaymentMethod: val as PaymentMethod })}
            >
              <SelectTrigger>
                <SelectValue placeholder={t("اختر طريقة الدفع", "Select payment method")} />
              </SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map((method) => (
                  <SelectItem key={method.value} value={method.value}>
                    {t(method.label, method.labelEn)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Payment Schedule Preview Table */}
          {paymentSchedule.length > 0 && (
            <div className="space-y-3">
              <Label className="text-base font-medium">{t("معاينة جدول الدفعات", "Payment Schedule Preview")}</Label>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="w-20 text-center">{t("رقم الدفعة", "Payment #")}</TableHead>
                      <TableHead>{t("تاريخ الاستحقاق", "Due Date")}</TableHead>
                      <TableHead className="text-end">{t("المبلغ", "Amount")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paymentSchedule.map((payment) => (
                      <TableRow key={payment.paymentNumber}>
                        <TableCell className="text-center font-medium">
                          {payment.paymentNumber}
                        </TableCell>
                        <TableCell>{formatDisplayDate(payment.dueDate)}</TableCell>
                        <TableCell className="text-end font-medium">
                          {formatNumber(payment.amount)}&nbsp;{RIYAL}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
